import { useContext, useEffect } from 'react';
import { Camelized } from 'humps';
import { useOtherSettings } from '@namespace/cms';
import type { ProviderIdToAdditionalParamsMap } from '@namespace/cms/src/api/getSiteSettings/schemes/siteSettings';
import { UserContext } from '@namespace/user';
import { gamesStoreActions, useGetIsLobbyDataLoaded } from '../../store';
import type { CasinoTypeUnion } from '../../types';
import { getGamesLiveData } from '../api';
import type { ISingleGameResponse } from '../api/getGamesLiveData';

const prepareLiveData = (
  liveDataItems: Camelized<ISingleGameResponse>[],
  providerIdToAdditionalParamsMap: ProviderIdToAdditionalParamsMap
) =>
  liveDataItems?.map((item) => ({
    ...item,
    ...(providerIdToAdditionalParamsMap?.[item.providerId] ?? {})
  }));

const { SET_GAMES_LIVE_DATA } = gamesStoreActions;

export const useGamesLiveData = (
  casinoType: CasinoTypeUnion,
  defaultFeedName: string
): void => {
  const [user] = useContext(UserContext);

  const { providersLiveDataConfig } = useOtherSettings();
  const { activeWallet = {} } = user;
  const { currency } = activeWallet;

  const { defaultCurrency, providerIdToAdditionalParamsMap } =
    providersLiveDataConfig;

  const currentCasinoTypeConfig = providersLiveDataConfig[casinoType];

  const {
    intervalSeconds: intervalSecondsFromConfig,
    providersServiceIds,
    providerPredefinedGames
  } = currentCasinoTypeConfig;

  const intervalSecondsPrepared =
    Number(intervalSecondsFromConfig) < 5
      ? 60
      : Number(intervalSecondsFromConfig);

  const isFeedLoaded = useGetIsLobbyDataLoaded(defaultFeedName, casinoType);

  useEffect(() => {
    if (
      (providersServiceIds?.length || providerPredefinedGames?.length) &&
      isFeedLoaded
    ) {
      const setGamesLiveData = async () => {
        const res = await getGamesLiveData({
          providerIds: providersServiceIds,
          currency: currency || defaultCurrency,
          internalGameIds: providerPredefinedGames
        });
        if (res.data?.items) {
          SET_GAMES_LIVE_DATA(
            prepareLiveData(res.data?.items, providerIdToAdditionalParamsMap)
          );
        }
      };
      setGamesLiveData();

      const intervalId = setInterval(
        setGamesLiveData,
        intervalSecondsPrepared * 1000
      );
      return () => clearInterval(intervalId);
    }
    return undefined;
  }, [isFeedLoaded, providersServiceIds, providerPredefinedGames]);
};
